// Nintendo Switch Pro Controller (VID 0x057E / PID 0x2009) WebHID driver.
//
// Over USB on macOS the OS hands the browser a controller whose HID descriptor
// is a copied-over Bluetooth descriptor (it lies about the report layout) and
// that never receives Nintendo's vendor handshake — so the standard Gamepad API
// sees garbage/nothing. WebHID lets us open the raw device, run the handshake +
// force USB-only mode, switch it into full-report mode (0x30), and decode the
// real input reports ourselves by fixed offset.
//
// Ported from macos-usb-controller/pro-controller.js. See its PROTOCOL.md.
// Ref: https://github.com/dekuNukem/Nintendo_Switch_Reverse_Engineering

import type { ArcadeControlCode } from '@/lib/arcade-controls';

const PRO_CONTROLLER_FILTER = {
  vendorId: 0x057e,
  productId: 0x2009,
} as const;

// Neutral rumble payload required as the prefix of every 0x01 subcommand report.
const NEUTRAL_RUMBLE = [0x00, 0x01, 0x40, 0x40, 0x00, 0x01, 0x40, 0x40];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface ProControllerButtons {
  A: boolean;
  B: boolean;
  X: boolean;
  Y: boolean;
  L: boolean;
  R: boolean;
  ZL: boolean;
  ZR: boolean;
  minus: boolean;
  plus: boolean;
  rStick: boolean;
  lStick: boolean;
  home: boolean;
  capture: boolean;
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
}

interface StickValue {
  x: number;
  y: number;
  raw: { x: number; y: number };
}

export interface ProControllerState {
  player: number;
  buttons: ProControllerButtons;
  leftStick: StickValue;
  rightStick: StickValue;
  battery: number;
}

export class ProController extends EventTarget {
  device: HIDDevice;
  player: number;
  lastState: ProControllerState | null = null; // last decoded state, for seeding
  private packet = 0; // 4-bit rolling counter for subcommand reports
  private lastReassert = 0; // throttle for full-mode re-assertion
  private onReport: (event: HIDInputReportEvent) => void;

  constructor(device: HIDDevice, { player = 1 }: { player?: number } = {}) {
    super();
    this.device = device;
    this.player = player; // 1-based slot, drives the lit LED
    this.onReport = (event) => this.handleInputReport(event);
  }

  async open() {
    if (!this.device.opened) {
      await this.device.open();
    }
    this.device.addEventListener('inputreport', this.onReport);
    await this.initialize();
  }

  async close() {
    this.device.removeEventListener('inputreport', this.onReport);
    if (this.device.opened) {
      await this.device.close();
    }
  }

  // --- raw "0x80" USB vendor commands ----------------------------------------
  private sendRaw(subcmd: number) {
    return this.device.sendReport(0x80, Uint8Array.of(subcmd));
  }

  // --- "0x01" rumble + subcommand reports ------------------------------------
  private sendSubcommand(subcmd: number, args: number[] = []) {
    this.packet = (this.packet + 1) & 0x0f;
    const data = Uint8Array.from([
      this.packet,
      ...NEUTRAL_RUMBLE,
      subcmd,
      ...args,
    ]);
    return this.device.sendReport(0x01, data);
  }

  // Single distinct LED per player so controllers are visually identifiable.
  private ledPattern() {
    return 1 << ((this.player - 1) % 4);
  }

  // Re-assign the player slot at runtime and update the LED.
  async setPlayer(player: number) {
    this.player = player;
    await this.sendSubcommand(0x30, [this.ledPattern()]);
  }

  private async initialize() {
    // 1. Handshake / lock the controller to USB so it won't time out to BT.
    await this.sendRaw(0x01); // request device info / MAC
    await sleep(20);
    await this.sendRaw(0x02); // handshake
    await sleep(20);
    await this.sendRaw(0x03); // bump baud to 3Mbit
    await sleep(20);
    await this.sendRaw(0x02); // re-handshake after baud change
    await sleep(20);
    await this.sendRaw(0x04); // force USB-only, disable HID timeout (critical)
    await sleep(20);

    // 2. Configure the controller.
    await this.sendSubcommand(0x30, [this.ledPattern()]); // player LED (stops blink)
    await sleep(20);
    await this.sendSubcommand(0x40, [0x01]); // enable IMU / gyro
    await sleep(20);
    await this.sendSubcommand(0x48, [0x01]); // enable rumble
    await sleep(20);
    await this.sendSubcommand(0x03, [0x30]); // input report mode 0x30 = full, 60Hz
  }

  // --- input decoding --------------------------------------------------------
  // WebHID strips the report ID from event.data, so all offsets here are shifted
  // down by 1 versus dekuNukem's tables. The controller's power-on default is
  // report 0x3f ("simple" mode), where the left stick is a D-pad hat with no
  // analog axes. It silently reverts to 0x3f on a USB reset — e.g. when another
  // controller enumerates on the same hub. On a 0x3f, push it back to 0x30.
  private reassertFullMode() {
    const now = Date.now();
    if (now - this.lastReassert < 500) {
      return;
    }
    this.lastReassert = now;
    this.sendSubcommand(0x03, [0x30]).catch(() => {});
  }

  private handleInputReport(event: HIDInputReportEvent) {
    if (event.reportId === 0x3f) {
      this.reassertFullMode();
      return;
    }
    if (event.reportId !== 0x30) {
      return; // ignore 0x21 subcommand acks, etc.
    }
    const d = new Uint8Array(event.data.buffer);

    const right = d[2]; // Y X B A SR SL R ZR
    const shared = d[3]; // - + Rstick Lstick Home Capture
    const left = d[4]; // Down Up Right Left SR SL L ZL

    const state: ProControllerState = {
      player: this.player,
      buttons: {
        A: !!(right & 0x08),
        B: !!(right & 0x04),
        X: !!(right & 0x02),
        Y: !!(right & 0x01),
        R: !!(right & 0x40),
        ZR: !!(right & 0x80),
        L: !!(left & 0x40),
        ZL: !!(left & 0x80),
        minus: !!(shared & 0x01),
        plus: !!(shared & 0x02),
        rStick: !!(shared & 0x04),
        lStick: !!(shared & 0x08),
        home: !!(shared & 0x10),
        capture: !!(shared & 0x20),
        up: !!(left & 0x02),
        down: !!(left & 0x01),
        left: !!(left & 0x08),
        right: !!(left & 0x04),
      },
      leftStick: decodeStick(d[5], d[6], d[7]),
      rightStick: decodeStick(d[8], d[9], d[10]),
      battery: (d[1] >> 4) & 0x0f, // 8=full .. 0=empty (even = charging)
    };

    this.lastState = state;
    this.dispatchEvent(new CustomEvent('input', { detail: state }));
  }
}

// Tracks any number of Pro Controllers: assigns player slots, lights distinct
// LEDs, restores already-granted devices, and handles plug/unplug hotplug.
//
// Events (CustomEvent, detail = ProController):
//   "add"    — a controller was connected + initialized
//   "remove" — a controller was unplugged
export class ProControllerManager extends EventTarget {
  controllers: ProController[] = [];
  private onConnectBound: (event: HIDConnectionEvent) => void;
  private onDisconnectBound: (event: HIDConnectionEvent) => void;

  constructor() {
    super();
    this.onConnectBound = (event) => {
      void this.onConnect(event);
    };
    this.onDisconnectBound = (event) => {
      void this.onDisconnect(event);
    };
    if ('hid' in navigator) {
      navigator.hid.addEventListener('connect', this.onConnectBound);
      navigator.hid.addEventListener('disconnect', this.onDisconnectBound);
    }
  }

  // Re-open controllers the user already granted in a previous session.
  async restore() {
    if (!('hid' in navigator)) {
      return;
    }
    const devices = await navigator.hid.getDevices();
    for (const device of devices) {
      if (matches(device)) {
        // Tolerate a single device failing to open (e.g. briefly held by the OS)
        // so the remaining already-granted controllers still reconnect.
        await this.add(device).catch(() => null);
      }
    }
  }

  // Prompt the user to authorize one (or more) controllers. Must be called from
  // within a user gesture (e.g. a click handler).
  async connect() {
    const devices = await navigator.hid.requestDevice({
      filters: [PRO_CONTROLLER_FILTER],
    });
    for (const device of devices) {
      await this.add(device);
    }
  }

  has(device: HIDDevice) {
    return this.controllers.some((c) => c.device === device);
  }

  // Remove the hid hotplug listeners and close every controller.
  async destroy() {
    if ('hid' in navigator) {
      navigator.hid.removeEventListener('connect', this.onConnectBound);
      navigator.hid.removeEventListener('disconnect', this.onDisconnectBound);
    }
    const controllers = this.controllers;
    this.controllers = [];
    for (const controller of controllers) {
      await controller.close().catch(() => {});
    }
  }

  // Lowest unused 1-based player slot.
  private nextPlayer() {
    const used = new Set(this.controllers.map((c) => c.player));
    let n = 1;
    while (used.has(n)) {
      n++;
    }
    return n;
  }

  private async add(device: HIDDevice) {
    if (!matches(device) || this.has(device)) {
      return null;
    }
    const controller = new ProController(device, { player: this.nextPlayer() });
    await controller.open();
    this.controllers.push(controller);
    this.dispatchEvent(new CustomEvent('add', { detail: controller }));
    return controller;
  }

  private async onConnect(event: HIDConnectionEvent) {
    // Fires for already-granted devices when physically plugged in.
    if (matches(event.device)) {
      await this.add(event.device);
    }
  }

  private async onDisconnect(event: HIDConnectionEvent) {
    const controller = this.controllers.find((c) => c.device === event.device);
    if (!controller) {
      return;
    }
    this.controllers = this.controllers.filter((c) => c !== controller);
    await controller.close().catch(() => {});
    this.dispatchEvent(new CustomEvent('remove', { detail: controller }));
  }
}

// Single shared manager for the whole client session. Controllers are opened
// once and survive page navigation + React strict-mode remounts, so a granted
// controller never gets torn down and re-prompted — restore() reconnects it
// silently after a full reload via the persisted WebHID permission.
let sharedManager: ProControllerManager | null = null;
let restorePromise: Promise<void> | null = null;

export function getProControllerManager(): ProControllerManager | null {
  if (typeof navigator === 'undefined' || !('hid' in navigator)) {
    return null;
  }
  if (!sharedManager) {
    sharedManager = new ProControllerManager();
  }
  return sharedManager;
}

// Kicks off the silent reconnect of already-granted controllers exactly once.
export function ensureControllersRestored(): Promise<void> {
  const manager = getProControllerManager();
  if (!manager) {
    return Promise.resolve();
  }
  if (!restorePromise) {
    restorePromise = manager.restore().catch(() => {});
  }
  return restorePromise;
}

function matches(device: HIDDevice) {
  return (
    device.vendorId === PRO_CONTROLLER_FILTER.vendorId &&
    device.productId === PRO_CONTROLLER_FILTER.productId
  );
}

// 12-bit packed stick -> normalized -1..1 (raw center ~2048, range ~0..4095).
function decodeStick(b0: number, b1: number, b2: number): StickValue {
  const rawX = b0 | ((b1 & 0x0f) << 8);
  const rawY = (b1 >> 4) | (b2 << 4);
  return {
    raw: { x: rawX, y: rawY },
    x: rawX / 2048 - 1,
    y: -(rawY / 2048 - 1), // flip so up is positive
  };
}

// --- Pro Controller -> arcade control mapping --------------------------------
// Directions come from the D-pad OR the analog left stick (past a deadzone).
// Action buttons use a fighting-stick style 6-button layout:
//   B1=Y B2=X B3=L / B4=B B5=A B6=R, Start=+, Back=- (minus, handled by caller).
const STICK_DEADZONE = 0.5;

interface MenuGamepad {
  buttons: boolean[];
  axes: number[];
}

function resolveDirections(state: ProControllerState) {
  const b = state.buttons;
  return {
    // Stick vertical is inverted relative to the D-pad on the cabinet, so the
    // analog up/down contribution is flipped here (D-pad stays as decoded).
    up: b.up || state.leftStick.y < -STICK_DEADZONE,
    down: b.down || state.leftStick.y > STICK_DEADZONE,
    left: b.left || state.leftStick.x < -STICK_DEADZONE,
    right: b.right || state.leftStick.x > STICK_DEADZONE,
  };
}

// Maps a controller to the standard-gamepad button layout the arcade menu polls
// (index 0 = confirm, 1 = toggle description, 9 = start, 12-15 = D-pad).
export function proControllerToMenuGamepad(
  state: ProControllerState,
): MenuGamepad {
  const dir = resolveDirections(state);
  const buttons = Array(17).fill(false) as boolean[];
  buttons[0] = state.buttons.A || state.buttons.Y; // confirm / play (A or B1=Y)
  buttons[1] = state.buttons.B; // toggle description
  buttons[9] = state.buttons.plus; // start = play
  buttons[12] = dir.up;
  buttons[13] = dir.down;
  buttons[14] = dir.left;
  buttons[15] = dir.right;
  return { buttons, axes: [0, 0, 0, 0] };
}

// Maps a controller to the per-player logical arcade controls a running game
// expects. Back/coin (minus) is intentionally excluded — the caller handles it.
export function proControllerToArcadeControls(
  state: ProControllerState,
  player: 1 | 2,
): Record<ArcadeControlCode, boolean> {
  const dir = resolveDirections(state);
  const b = state.buttons;
  const p = player === 1 ? 'P1' : 'P2';

  return {
    [`${p}_U`]: dir.up,
    [`${p}_D`]: dir.down,
    [`${p}_L`]: dir.left,
    [`${p}_R`]: dir.right,
    [`${p}_1`]: b.Y,
    [`${p}_2`]: b.X,
    [`${p}_3`]: b.L,
    [`${p}_4`]: b.B,
    [`${p}_5`]: b.A,
    [`${p}_6`]: b.R,
    [`START${player}`]: b.plus,
  } as Record<ArcadeControlCode, boolean>;
}
